import domReady from '@wordpress/dom-ready';
import { createRoot } from '@wordpress/element';
import React from 'react';
import BannerSlot, {
	isEligibleInBrowser,
} from './components/stickyBanner/BannerSlot';
import { isReturningVisitor } from './components/stickyBanner/displayRules.helper';
import { getTemplateFonts } from './components/stickyBanner/templates';
import {
	loadBannerFonts,
	normalizeSettings,
	normalizeTemplate,
	readSiteTimezone,
} from './components/stickyBanner/stickyBanner.helper';
import { BannerPosition } from './components/stickyBanner/types';

declare const window: Window & { wplms_admin_support_frontend?: unknown };

const frontendData = ( window.wplms_admin_support_frontend || {} ) as {
	sticky_banners?: unknown;
};

// PHP sends only the banners whose page / visitor rules match this request,
// highest priority first.
const banners = (
	Array.isArray( frontendData.sticky_banners )
		? frontendData.sticky_banners
		: []
)
	.map( ( raw ) => ( {
		banner: normalizeSettings( raw ),
		// The banner's template (null from PHP = base template defaults).
		design: normalizeTemplate(
			raw && typeof raw === 'object'
				? ( raw as { design?: unknown } ).design
				: null
		),
	} ) )
	.filter( ( item ) => item.banner.id && item.banner.enabled );

const siteTimezone = readSiteTimezone( window.wplms_admin_support_frontend );

/**
 * Mount the winning banner of a position into its own container.
 *
 * @param position    Top or bottom.
 * @param isReturning Whether the visitor is returning.
 */
const mountPosition = ( position: BannerPosition, isReturning: boolean ) => {
	// First eligible banner wins (list is sorted by priority).
	const match = banners.find(
		( item ) =>
			item.banner.displayRules.placement.position === position &&
			isEligibleInBrowser(
				item.banner,
				item.design,
				siteTimezone,
				isReturning
			)
	);
	if ( ! match ) {
		return;
	}
	const { banner, design } = match;

	const id = `wplms-sticky-banner-${ position }`;

	loadBannerFonts( design.typography, {
		extraFamilies: getTemplateFonts( design ),
		linkId: `${ id }-fonts`,
	} );
	let container = document.getElementById( id );
	if ( ! container ) {
		container = document.createElement( 'div' );
		container.id = id;
		container.className = 'wplms-sticky-banner-root';
		if ( position === 'top' ) {
			document.body.prepend( container );
		} else {
			document.body.append( container );
		}
	}

	createRoot( container ).render(
		<BannerSlot
			banner={ banner }
			design={ design }
			siteTimezone={ siteTimezone }
		/>
	);
};

domReady( () => {
	if ( ! banners.length ) {
		return;
	}
	const isReturning = isReturningVisitor();
	mountPosition( 'top', isReturning );
	mountPosition( 'bottom', isReturning );
} );
